import { AnalyticsService } from './../../services/analytics.service';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, effect, ElementRef, HostListener, inject, OnDestroy, PLATFORM_ID, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PresaleDataService } from '../../services/connection-services/data.service';
import { EventService } from '../../services/event.service';
import { PixelService } from '../../services/pixel.service';
import {
  CHAINS,
} from '../../shared/constants/app-enums';
import {
  CONNECT_WALLET_DELAY_MS,
  HEADER_NAV_HIGHLIGHT_DELAY_MS,
  HEADER_SCROLL_THRESHOLD_PX,
  MENU_CLOSE_ANIMATION_MS,
  MENU_OPEN_ANIMATION_MS,
} from '../../shared/constants/app-config.const';
import { allLanguages } from '../../shared/constants/languages';
import { ILanguage } from '../../shared/interfaces/wallet.interface';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
})
export class HeaderComponent implements AfterViewInit, OnDestroy {
  readonly navigation = viewChild.required<ElementRef>('navigation');
  public walletAddress: string = '';
  isScrolled: boolean = false;
  showMenu: boolean = false;
  openMenu: boolean = false;
  modalRef?: BsModalRef;
  hashKey: string = '';
  server: string = environment.server;
  currentLanguage = '';
  languagesList = allLanguages;
  selectedLanguage: ILanguage = {} as ILanguage;
  isStaking = false;
  isPresalestarted = false;

  /** Update scroll state — uses DOCUMENT defaultView to avoid direct window access */
  @HostListener('window:scroll')
  checkScroll() {
    this.isScrolled = (this.document.defaultView?.scrollY ?? 0) >= HEADER_SCROLL_THRESHOLD_PX;
  }

  navigations = [
    { routeName: 'rewards', navName: 'top_navigation.nav_rewards' },
    { routeName: 'protocol', navName: 'top_navigation.nav_protocol' },
    { routeName: 'dao', navName: 'top_navigation.nav_dao' },
    { routeName: 'roadmap', navName: 'top_navigation.nav_roadmap' },
    { routeName: 'tokenomics', navName: 'top_navigation.nav_tokenomics' },
    // { routeName: 'analytics', navName: 'top_navigation.nav_analytics' },
    // { routeName: 'activity', navName: 'top_navigation.nav_activity' },
    // { routeName: 'docs', navName: 'top_navigation.nav_docs' },
  ];

  social = [
    {
      img: 'assets/images/svg-icons/social-icons/telegram.svg',
      alt: 'Telegram',
      link: 'https://t.me/seerdexofficial',
    },
    {
      img: 'assets/images/svg-icons/social-icons/x.svg',
      alt: 'Twitter',
      link: 'https://x.com/seerdexmarkets',
    },
    {
      img: 'assets/images/svg-icons/social-icons/discord.svg',
      alt: 'Discord',
      link: 'https://discord.com/invite/ep2p9PWNk',
    },

  ];

  private readonly _platformId = inject(PLATFORM_ID);
  // Use the DOCUMENT token rather than the global `document` for SSR safety
  private readonly document = inject(DOCUMENT);
  private readonly eventService = inject(EventService);
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(BsModalService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dataService = inject(PresaleDataService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly pixelService = inject(PixelService);

  constructor() {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe((params: LangChangeEvent) => {
      this.currentLanguage = params.lang;
      this.selectedLanguage = this.languagesList.filter((x) => x.value === this.currentLanguage)[0];
      if (isPlatformBrowser(this._platformId)) localStorage.setItem('lang', this.currentLanguage);
    });
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd), takeUntilDestroyed()).subscribe((event: NavigationEnd) => {
      this.isStaking = event.url.includes('staking');
    });
    this.activatedRoute.fragment.pipe(takeUntilDestroyed()).subscribe((fragment) => {
      this.hashKey = fragment ?? '';
    });
    effect(() => {
      // console.log("walletAddressSignal triggerd", this.dataService)
      this.dataService.walletAddressSignal();
      this.walletAddress = this.dataService?.activeChain === CHAINS.SOL ? this.dataService.walletAddressSOL : this.dataService.walletAddress;
    });
    this.checkPresaleStatus();
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this._platformId)) {
      if (!this.currentLanguage) {
        this.currentLanguage = localStorage.getItem('lang') || 'en';
        this.selectedLanguage = this.languagesList.filter((x) => x.value === this.currentLanguage)[0];
      }

      // Use DOCUMENT to access pathname and hash — avoids direct window.location reference
      const loc = this.document.location;
      const pathName = loc?.pathname.split('/')[2] || loc?.pathname.split('/')[1] || '';
      this.hashKey = loc?.hash ? loc.hash.substring(1) : '';
      // Delay allows the view to fully render before highlighting the active nav link
      setTimeout(() => this.addActive(this.navigation().nativeElement, pathName, true), HEADER_NAV_HIGHLIGHT_DELAY_MS);
    }
  }

  addActive(event: Event, section: string, autoMode: boolean = false) {
    if (!autoMode) this.hashKey = section && section != 'staking' ? section : '';
    const target: HTMLElement = event.currentTarget ? (event.currentTarget as HTMLElement) : (event as any);
    let linkList = target.parentNode?.querySelectorAll('a') || [];
    linkList.forEach((link: HTMLAnchorElement) => {
      if (link === event.target || link.title === this.hashKey || link === target) {
        link.classList.add('active');
      } else {
        link.classList.remove('active');
      }
    });
  }

  showWidget(section: string): void {
    /* this.analyticsService.vwoData('walletConnectionPlacement', { placement: 'Navbar' });
     // Use DOCUMENT.location rather than window.location for SSR compatibility
     if (this.document.location?.pathname.includes('dashboard')) {
       this.document.defaultView?.scroll({ top: 0 });
       this.connectModal();
     } else {
       this.eventService.showBuyWidget(section);
       // Brief delay allows the widget to appear before the wallet modal opens
       setTimeout(() => {
         this.dataService.connectWallet();
       }, CONNECT_WALLET_DELAY_MS);
     }*/
  }

  connectModal(): void {
    this.dataService.connectWallet();
  }

  disconnect(): void {
    this.dataService.disconnectWallet();
  }

  openCloseMenu(): void {
    if (this.showMenu) {
      this.openMenu = !this.openMenu;
      // Remove body scroll lock applied when menu opened
      this.document.body.classList.remove('modal-open');
      // Wait for CSS close animation before hiding the menu element
      setTimeout(() => (this.showMenu = !this.showMenu), MENU_CLOSE_ANIMATION_MS);
    } else {
      this.showMenu = !this.showMenu;
      // Lock body scroll while menu is visible
      this.document.body.classList.add('modal-open');
      // Wait for CSS open animation before showing the overlay
      setTimeout(() => (this.openMenu = !this.openMenu), MENU_OPEN_ANIMATION_MS);
    }
  }

  vwoData(item: { alt: string; link: string }): void {
    // this.analyticsService.vwoData('socials', { clickedXButton: item.alt === 'Twitter', clickedTelegramButton: item.alt === 'Telegram' });
    // this.pixelService.trackPageClick(item.link, item.alt.toLowerCase());
  }

  checkPresaleStatus(): void {
    const presaleStartdate: number = 1778763600000;
    if (presaleStartdate > Date.parse(new Date().toString())) {
      return;
    }
    this.isPresalestarted = true;
  }

  ngOnDestroy(): void { }
}
